/**
 * `SaveScenePresetDialog` (WP-1).
 *
 * Tokenized Material 3 dialog that replaces the legacy
 * `window.prompt('Name this view', ...)` flow in
 * `components/map/SavedViewsControl.tsx` and the parallel store-backed
 * panel in `app/TwinProductUx.TwinSavedViewsPanel`.
 *
 * Captures (visible layer ids, opacities, camera, time) and persists the
 * preset via `gtwinClient.scene.presets.upsert` (then `activate`) per
 * masterplan §5.4. Optimistic UI / rollback semantics live in the pure
 * `upsertScenePreset` controller for testability; this component is the
 * surface only.
 */

import { useId, useMemo, useState } from 'react';
import {
  Banner,
  Dialog,
  Icon,
  tokens,
  type DialogAction,
} from '@/design';
import type {
  ScenePresetInput,
  ScenePresetsAdapter,
  SceneSlot,
} from '@/sdk-stub/gtwin-sdk';
import {
  upsertScenePreset,
  type OptimisticHandle,
  type ScenePresetCaptureInput,
} from './upsertScenePreset';
import { useSyncStatusStore } from '@/state/useSyncStatusStore';

const ALL_SLOTS: ReadonlyArray<SceneSlot> = [
  'basemaps',
  'terrain',
  'realityData',
  'engineeringModels',
  'featureLayers',
  'overlays',
];

export interface SaveScenePresetCapture {
  readonly visibleLayerIds: ReadonlyArray<string>;
  readonly layerOpacity?: Readonly<Record<string, number>>;
  readonly visibleSlots?: ReadonlyArray<SceneSlot>;
  readonly camera?: ScenePresetInput['camera'];
  readonly time?: ScenePresetInput['time'];
}

export interface SaveScenePresetDialogProps {
  readonly gtwinId: string;
  readonly capture: SaveScenePresetCapture;
  readonly adapter: ScenePresetsAdapter;
  readonly optimistic: OptimisticHandle<ScenePresetInput>;
  readonly existingPresetIds?: ReadonlyArray<string>;
  readonly initialLabel?: string;
  readonly onSaved: (preset: ScenePresetInput, options: { activated: boolean }) => void;
  readonly onClose: () => void;
  readonly 'data-testid'?: string;
}

const inputStyle = {
  width: '100%',
  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
  borderRadius: tokens.radius.input,
  border: tokens.border.thin,
  background: tokens.color.surface[0],
  color: tokens.color.ink.primary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  minHeight: tokens.density.touchMin,
  boxSizing: 'border-box' as const,
};

const labelStyle = {
  display: 'block',
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.role.label.size,
  fontWeight: tokens.font.weight.medium,
  color: tokens.color.ink.secondary,
  marginBottom: tokens.space[1],
};

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function isCollision(id: string, existing: ReadonlyArray<string> | undefined): boolean {
  if (!existing) return false;
  return existing.includes(id);
}

export function SaveScenePresetDialog({
  gtwinId,
  capture,
  adapter,
  optimistic,
  existingPresetIds,
  initialLabel,
  onSaved,
  onClose,
  'data-testid': testId,
}: SaveScenePresetDialogProps) {
  const formId = useId();
  const [label, setLabel] = useState(initialLabel ?? '');
  const [overwrite, setOverwrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trimmedLabel = label.trim();
  const presetId = trimmedLabel ? `scn-${slugify(trimmedLabel)}` : '';
  const collides = isCollision(presetId, existingPresetIds) && !overwrite;

  const captureSummary = useMemo(() => {
    const slots = capture.visibleSlots ?? ALL_SLOTS;
    return {
      slotCount: slots.length,
      layerCount: capture.visibleLayerIds.length,
      hasCamera: Boolean(capture.camera),
      hasTime: Boolean(capture.time),
    };
  }, [capture]);

  async function handleSubmit() {
    if (!trimmedLabel || collides || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const visibleSlots: ReadonlyArray<SceneSlot> = capture.visibleSlots ?? ALL_SLOTS;
    const visibilityMap: Record<string, boolean> = {};
    for (const id of capture.visibleLayerIds) visibilityMap[id] = true;

    const presetInput: ScenePresetCaptureInput = {
      id: presetId,
      label: trimmedLabel,
      show: visibleSlots,
      layerVisibility: visibilityMap,
      layerOpacity: capture.layerOpacity,
      camera: capture.camera,
      time: capture.time,
    };

    try {
      const result = await upsertScenePreset({
        gtwinId,
        preset: presetInput,
        adapter,
        optimistic,
      });
      onSaved(result.preset, { activated: result.activated });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
      // WP-14 — classify typed real-backend errors so the global
      // `<RealBackendBanner>` lights up alongside the dialog's local
      // banner.
      useSyncStatusStore.getState().recordRealBackendError(err);
    } finally {
      setSubmitting(false);
    }
  }

  const actions: DialogAction[] = [
    { label: 'Cancel', variant: 'ghost', onClick: onClose },
    {
      label: submitting ? 'Saving…' : 'Save scene preset',
      variant: 'primary',
      onClick: handleSubmit,
    },
  ];

  return (
    <Dialog
      title="Save scene preset"
      size="sm"
      onClose={submitting ? () => undefined : onClose}
      actions={actions}
      data-testid={testId ?? 'save-scene-preset-dialog'}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
        <p
          style={{
            margin: 0,
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}
        >
          Captures the current visibility, opacities, camera, and time window
          and writes them to the gTwin's scene presets.
        </p>

        <div>
          <label htmlFor={`${formId}-label`} style={labelStyle}>
            Preset name
          </label>
          <input
            id={`${formId}-label`}
            type="text"
            autoFocus
            value={label}
            onChange={(event) => setLabel(event.currentTarget.value)}
            placeholder="e.g. Plant inspection — south rail"
            style={inputStyle}
            data-testid="save-preset-label"
          />
          {presetId ? (
            <span
              style={{
                display: 'block',
                marginTop: tokens.space[1],
                color: tokens.color.ink.muted,
                fontSize: tokens.font.role.engMeta.size,
                fontFamily: tokens.font.family.mono,
              }}
            >
              Preset id: {presetId}
            </span>
          ) : null}
        </div>

        <div
          aria-label="Preset capture summary"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: tokens.space[2],
            padding: tokens.space[3],
            borderRadius: tokens.radius.md,
            background: tokens.color.surface[2],
            border: tokens.border.thin,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.engMeta.size,
            color: tokens.color.ink.secondary,
          }}
        >
          <SummaryItem icon="layers" label={`${captureSummary.layerCount} visible layer${captureSummary.layerCount === 1 ? '' : 's'}`} />
          <SummaryItem icon="visibility" label={`${captureSummary.slotCount} slot${captureSummary.slotCount === 1 ? '' : 's'}`} />
          <SummaryItem
            icon="explore"
            label={captureSummary.hasCamera ? 'Camera captured' : 'Camera unset'}
          />
          <SummaryItem
            icon="schedule"
            label={captureSummary.hasTime ? 'Time captured' : 'No time window'}
          />
        </div>

        {collides ? (
          <Banner
            severity="warning"
            title="A preset with this name already exists"
            body={
              <>
                <span>
                  Saving will overwrite preset id <code>{presetId}</code>. Tick
                  the box below to confirm.
                </span>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: tokens.space[2],
                    marginTop: tokens.space[2],
                    fontSize: tokens.font.size.sm,
                    color: tokens.color.ink.primary,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={overwrite}
                    onChange={(event) => setOverwrite(event.currentTarget.checked)}
                    data-testid="save-preset-overwrite"
                  />
                  Overwrite existing preset
                </label>
              </>
            }
          />
        ) : null}

        {submitError ? (
          <Banner
            severity="error"
            title="Save preset failed"
            body={submitError}
            errorCode="WP1-SAVE-PRESET"
          />
        ) : null}
      </div>
    </Dialog>
  );
}

function SummaryItem({ icon, label }: { icon: Parameters<typeof Icon>[0]['name']; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
      }}
    >
      <Icon name={icon} size={16} decorative />
      <span>{label}</span>
    </span>
  );
}
